import { ChangeDetectionStrategy, Component, inject, output, input, model, signal, viewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { Dialog } from 'primeng/dialog';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import {
    BeefreeTemplateEditorComponent,
    IBeefreeTemplateEditorConfig
} from '../../../shared/third-party/beefree/beefree-template-editor.component';
import {
    MailluneTemplateEditorComponent,
    IMailluneTemplateEditorConfig,
    IMailluneTemplateSaved
} from '../../../shared/third-party/maillune/maillune-template-editor.component';
import { ToastService } from '../../../../../services/toast.service';

@Component({
    selector: 'email-editor-drawer',
    templateUrl: './email-editor-drawer.html',
    styleUrl: './email-editor-drawer.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Drawer, TranslatePipe, BeefreeTemplateEditorComponent, MailluneTemplateEditorComponent, ButtonModule, Dialog, FloatLabel, InputText, FormsModule]
})
export class EmailEditorDrawerComponent {
    private toast = inject(ToastService);
    private translate = inject(TranslateService);

    visible = model<boolean>(false);

    beefreeConfig = input<IBeefreeTemplateEditorConfig | null>(null);
    // When set, the Maillune editor is shown instead of Beefree (full replacement, per-org).
    mailluneConfig = input<IMailluneTemplateEditorConfig | null>(null);
    // Maillune exposes the static placeholders as merge-tag pills; the drawer button then only
    // offers the dynamic ones (promo code / booking link), which only triggers can resolve.
    showDynamicPlaceholders = input<boolean>(true);
    // Widened to carry Maillune's thumbnail through to the host component.
    templateSaved = output<IMailluneTemplateSaved | { json: string; html: string }>();
    cancelled = output<void>();
    showPlaceholders = output<void>();

    beefreeTemplateEditor = viewChild<BeefreeTemplateEditorComponent>('beefreeTemplateEditor');
    mailluneTemplateEditor = viewChild<MailluneTemplateEditorComponent>('mailluneTemplateEditor');
    drawer = viewChild<Drawer>('drawer');

    saveAsTemplateDialogVisible = signal(false);
    saveAsTemplateName = signal('');
    savingAsTemplate = signal(false);
    saving = signal(false);

    async save(): Promise<void> {
        this.saving.set(true);
        try {
            if (this.mailluneTemplateEditor()) {
                await this.mailluneTemplateEditor()?.save();
            } else {
                await this.beefreeTemplateEditor()?.save();
            }
        } catch (e) {
            console.error('Failed to save email template:', e);
            this.toast.showError(this.translate.instant('TEMPLATE_SAVE_FAILED'));
        } finally {
            this.saving.set(false);
        }
    }

    openTemplates(): void {
        this.mailluneTemplateEditor()?.openTemplates();
    }

    openSaveAsTemplateDialog(): void {
        this.saveAsTemplateName.set('');
        this.saveAsTemplateDialogVisible.set(true);
    }

    cancelSaveAsTemplate(): void {
        this.saveAsTemplateDialogVisible.set(false);
    }

    async confirmSaveAsTemplate(): Promise<void> {
        const name = this.saveAsTemplateName().trim();
        const editor = this.mailluneTemplateEditor();
        if (!name || !editor) return;
        this.savingAsTemplate.set(true);
        try {
            await editor.saveAsTemplate(name);
            this.toast.showSuccess(this.translate.instant('TEMPLATE_SAVED'));
            this.saveAsTemplateDialogVisible.set(false);
        } catch (e) {
            console.error('Failed to save Maillune template:', e);
            this.toast.showError(this.translate.instant('TEMPLATE_SAVE_FAILED'));
        } finally {
            this.savingAsTemplate.set(false);
        }
    }

    onTemplateSaved(data: IMailluneTemplateSaved | { json: string; html: string }): void {
        this.templateSaved.emit(data);
        this.cleanupAndClose();
    }

    cancel(): void {
        this.cancelled.emit();
        this.cleanupAndClose();
    }

    // PrimeNG goes crazy due to async beefree calls. This forces a full close.
    private cleanupAndClose(): void {
        this.drawer()?.destroyModal();
        this.visible.set(false);
        this.saveAsTemplateDialogVisible.set(false);
    }
}
